import React, { PropsWithChildren } from "react";
import { useHotkeys } from "domains/commons/contexts/HotkeysProvider";
import isBreakpointMobile from "domains/commons/isBreakpointMobile";
import { TOP_BAR_HEIGHT } from "domains/navigation/constants/TopBar";
import { appShortcuts } from "domains/shortcuts/components/appShortcuts";

import {
  Box,
  BoxProps,
  Flex,
  StackProps,
  useBreakpoint,
  VStack,
} from "@chakra-ui/react";

interface GeneratorContainerProps extends BoxProps {
  onSubmit: () => void;
  isMobile?: boolean;
}

export function GeneratorContainer({
  onSubmit,
  isMobile: overrideIsMobile,
  ...props
}: GeneratorContainerProps) {
  const breakpoint = useBreakpoint({ ssr: true });
  const isMobile = overrideIsMobile ?? isBreakpointMobile(breakpoint);

  useHotkeys(
    appShortcuts.global.shortcuts.launchGeneration.shortcut,
    () => {
      if (document.activeElement) {
        (document.activeElement as HTMLElement).blur();
      }
      onSubmit();
    },
    {
      enableOnFormTags: true,
    },
    [onSubmit]
  );

  return (
    <Flex
      direction={isMobile ? "column" : "row"}
      h={`calc(100vh - ${TOP_BAR_HEIGHT}px)`}
      {...props}
    />
  );
}

// ------------------------------------

interface GeneratorSidebarProps extends StackProps {
  isMobile?: boolean;
}

export function GeneratorSidebar({
  isMobile: overrideIsMobile,
  ...props
}: GeneratorSidebarProps) {
  const breakpoint = useBreakpoint({ ssr: true });
  const isMobile = overrideIsMobile ?? isBreakpointMobile(breakpoint);
  return (
    <VStack
      pos="relative"
      zIndex="calc(var(--chakra-zIndices-sticky) + 1)"
      align="stretch"
      overflow="auto"
      overflowX={"auto"}
      w={isMobile ? "100%" : "375px"}
      h={isMobile ? "50%" : "100%"}
      borderRightWidth={isMobile ? 0 : 1}
      borderBottomWidth={isMobile ? 1 : 0}
      bgColor="secondary.900"
      spacing={0}
      {...props}
    />
  );
}

// ------------------------------------

interface GeneratorContentProps extends PropsWithChildren {
  scrollRef: React.RefObject<HTMLDivElement>;
}

export function GeneratorContent({
  scrollRef,
  children,
}: GeneratorContentProps) {
  return (
    <VStack
      ref={scrollRef}
      align="stretch"
      flex={1}
      overflowX="hidden"
      overflowY="auto"
      px={8}
      spacing={2}
    >
      <Box h="full" mb={4}>
        {children}
      </Box>
    </VStack>
  );
}
